import React, { useCallback, useEffect, useState } from 'react';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import ReportStockItemLink from '../../components/reports/ReportStockItemLink';
import usePageTitle from '../../utils/usePageTitle';
import { useToast } from '../../contexts/ToastContext';
import { toReportExportAction } from '../../components/ReportExportButton';
import { reportsService } from '../../services/reportsService';
import { warehousesService } from '../../services/productionService';
import { downloadReportCsv } from '../../utils/reportExport';
import FilterDrawer, { toFilterAction } from '../../components/FilterDrawer';
import useReportFilters from '../../utils/useReportFilters';
import { Input } from '../../components/ui/input';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { cn, formatQuantity } from '../../lib/utils';

function ProjectedStockReport() {
    usePageTitle('Saldo Atual e Projetado por Depósito');
    const { toastApiError } = useToast();

    const [warehouses, setWarehouses] = useState([]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const filters = useReportFilters({
        code: '',
        item: '',
        warehouseId: '',
        belowMinimum: false,
        approvedOnly: false,
    });

    useEffect(() => {
        (async () => {
            try {
                const response = await warehousesService.list({ per_page: 1000 });
                setWarehouses(response.data.data || response.data || []);
            } catch (e) {
                console.error('Error loading warehouses:', e);
            }
        })();
    }, []);

    const buildParams = useCallback(() => {
        const f = filters.applied;
        return {
            code: f.code || undefined,
            item: f.item || undefined,
            warehouse_id: f.warehouseId || undefined,
            below_minimum: f.belowMinimum ? 1 : 0,
            approved_only: f.approvedOnly ? 1 : 0,
        };
    }, [filters.applied]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const response = await reportsService.projectedStock(buildParams());
            setRows(Array.isArray(response.data) ? response.data : []);
        } catch (e) {
            console.error('Error loading projected stock report:', e);
            toastApiError(e, { title: 'Erro ao carregar', fallback: 'Não foi possível carregar o relatório' });
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildParams]);

    useEffect(() => {
        load();
    }, [load]);

    const handleExport = async () => {
        setExporting(true);
        try {
            await downloadReportCsv(reportsService.projectedStockCsv(buildParams()), 'saldo-projetado.csv');
        } catch (e) {
            console.error('Error exporting report:', e);
            toastApiError(e, { title: 'Erro ao exportar', fallback: 'Não foi possível exportar o relatório' });
        } finally {
            setExporting(false);
        }
    };

    const thBase = 'h-[56px] text-sm font-medium text-gray-500 text-center p-0';
    const tdBase = 'h-[56px] text-sm text-gray-800 p-0';
    const tdEnd = 'justify-end tabular-nums text-right';
    const ROW_HEIGHT = 56;
    const thInner = 'h-[56px] min-h-[56px] w-full border-r border-b border-gray-200 px-2 box-border flex items-center justify-center';
    const tdInner = 'h-[56px] min-h-[56px] w-full border-r border-b border-gray-200 px-2 box-border flex items-center';

    const colWidths = [300, 160, 130, 280, 150, 150];
    const colLeft = [0, colWidths[0]];

    const stickyTop = { position: 'sticky', top: 0, zIndex: 10, background: '#f9fafb' };
    const stickyTopWithLeft = (left, width) => ({ ...stickyTop, left, minWidth: width, width, zIndex: 11 });
    const stickyBody = (left, width) => ({ position: 'sticky', left, minWidth: width, width, background: '#fff', zIndex: 5 });
    const normalCol = (width) => ({ minWidth: width, width });
    const totalCols = 6;

    return (
        <AppLayout>
            <div className="h-[calc(100vh-8rem)] flex flex-col gap-0">
                <PageHeader
                    title="Saldo Atual e Projetado por Depósito"
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Relatórios' },
                    ]}
                    additionalActions={[toFilterAction({ count: filters.count, onClick: filters.openDrawer })]}
                    actions={[
                        toReportExportAction({ onExport: handleExport, disabled: loading, exporting }),
                    ]}
                />
                <div className="overflow-auto flex-1 min-h-0 -mx-4 md:-mx-8 -mb-8">
                    {loading && rows.length === 0 ? null : (
                        <table className="w-full caption-bottom text-sm">
                            <TableHeader className="border-b-0 bg-transparent">
                                <TableRow className="border-b-0 hover:bg-transparent" style={{ height: ROW_HEIGHT }}>
                                    <TableHead className={thBase} style={stickyTopWithLeft(colLeft[0], colWidths[0])}>
                                        <div className={cn(thInner, 'report-first-col')}>Item</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={stickyTopWithLeft(colLeft[1], colWidths[1])}>
                                        <div className={thInner}>Depósito</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[2]) }}>
                                        <div className={cn(thInner, 'justify-end')}>Saldo Atual</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[3]) }}>
                                        <div className={thInner}>Pedidos</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[4]) }}>
                                        <div className={cn(thInner, 'justify-end')}>Qtd. Pendente</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[5]) }}>
                                        <div className={cn(thInner, 'justify-end border-r-2 border-r-gray-300')}>Saldo Projetado</div>
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow className="border-b-0 hover:bg-transparent">
                                        <TableCell colSpan={totalCols} className="text-center py-8 text-gray-500">
                                            Nenhum produto encontrado
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map((row) => {
                                        const isBelowMin = row.saldo_projetado < (row.min_stock ?? 0);
                                        return (
                                            <TableRow key={row.product_id} className="border-b-0 hover:bg-transparent" style={{ height: ROW_HEIGHT }}>
                                                <TableCell className={tdBase} style={stickyBody(colLeft[0], colWidths[0])}>
                                                    <div className={cn(tdInner, 'report-first-col', 'min-w-0')}>
                                                        <ReportStockItemLink productId={row.product_id} className="truncate block">
                                                            {row.code} - {row.description}
                                                        </ReportStockItemLink>
                                                    </div>
                                                </TableCell>
                                                <TableCell className={tdBase} style={stickyBody(colLeft[1], colWidths[1])}>
                                                    <div className={tdInner}>{row.warehouse}</div>
                                                </TableCell>
                                                <TableCell className={tdBase} style={normalCol(colWidths[2])}>
                                                    <div className={cn(tdInner, tdEnd)}>{formatQuantity(row.saldo_atual)}</div>
                                                </TableCell>
                                                <TableCell className={tdBase} style={normalCol(colWidths[3])}>
                                                    <div className={cn(tdInner, 'min-w-0')}>
                                                        <span className="truncate" title={row.pedidos}>{row.pedidos || '-'}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className={tdBase} style={normalCol(colWidths[4])}>
                                                    <div className={cn(tdInner, tdEnd)}>{formatQuantity(row.quantidade_pendente)}</div>
                                                </TableCell>
                                                <TableCell className={tdBase} style={normalCol(colWidths[5])}>
                                                    <div className={cn(tdInner, tdEnd, 'border-r-2 border-r-gray-300', isBelowMin && 'text-rose-600 font-medium')}>
                                                        {formatQuantity(row.saldo_projetado)}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </table>
                    )}
                </div>
            </div>
            <FilterDrawer open={filters.open} onClose={filters.close} onApply={filters.apply} onClear={filters.clear}>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Código Item</label>
                    <Input value={filters.draft.code} onChange={(e) => filters.set('code', e.target.value)} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome Item</label>
                    <Input value={filters.draft.item} onChange={(e) => filters.set('item', e.target.value)} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Depósito</label>
                    <select
                        className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={filters.draft.warehouseId}
                        onChange={(e) => filters.set('warehouseId', e.target.value)}
                    >
                        <option value="">Depósito padrão do produto</option>
                        {warehouses.map((w) => (
                            <option key={w.id} value={w.id}>{w.name}</option>
                        ))}
                    </select>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={filters.draft.belowMinimum} onChange={(e) => filters.set('belowMinimum', e.target.checked)} />
                    Abaixo do mínimo
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={filters.draft.approvedOnly} onChange={(e) => filters.set('approvedOnly', e.target.checked)} />
                    Só aprovados
                </label>
            </FilterDrawer>
        </AppLayout>
    );
}

export default ProjectedStockReport;
